/**
 * Public-site content for maddonutshop.com.
 *
 * Plain data, no JSX and no browser APIs, so the same objects feed the React
 * pages, the structured data and the build-time SEO prerender (which imports
 * this file directly in Node — hence the .mjs extension).
 *
 * Facts here mirror what the business already publishes or what the ordering
 * portal already enforces (the two-business-day lead time comes from the
 * ordering-deadline setting) — nothing is invented for SEO.
 */

export const business = {
  name: 'MAD Donuts',
  legalName: 'MAD Donuts',
  tagline: 'Because we deserve better donuts',
  email: 'info@maddonutshop.com',
  baseTown: 'Pleasantville, NY',
  locality: 'Pleasantville',
  addressLine: 'Pleasantville, Westchester, NY',
  region: 'Westchester County, NY',
  nearby: 'nearby Connecticut',
  leadTime: '2 business days',
  description:
    'Award-winning wholesale donuts baked in Pleasantville, Westchester, NY and delivered fresh to cafés, restaurants and offices across Westchester County and nearby Connecticut.',
};

export const social = [
  { platform: 'Instagram', url: 'https://www.instagram.com/maddonutshop' },
  { platform: 'Facebook', url: 'https://www.facebook.com/maddonutshop' },
];

export const serviceArea = [
  'Pleasantville, NY',
  'Dobbs Ferry, NY',
  'Irvington, NY',
  'Tarrytown, NY',
  'Eastchester, NY',
  'Bronxville, NY',
  'Pelham, NY',
  'Larchmont, NY',
  'Valhalla, NY',
  'White Plains, NY',
  'Peekskill, NY',
  'Mount Kisco, NY',
  'Greenwich, CT',
];

export const awards = [
  {
    year: '2023',
    title: 'Best of Westchester — Best Donuts',
    source: 'Westchester Magazine',
    image: { src: '/img/award-bow-2023.webp', width: 512, height: 512, alt: 'Westchester Magazine Best of Westchester 2023 winner badge' },
  },
  {
    year: '2020',
    title: 'Best of Westchester — Best Donuts',
    source: 'Westchester Magazine',
    image: { src: '/img/award-bow-2020.webp', width: 225, height: 225, alt: 'Westchester Magazine Best of Westchester 2020 winner badge' },
  },
];

export const heroImage = {
  src: '/img/hero-apple-fritter.webp',
  width: 1600,
  height: 2000,
  alt: 'MAD Donuts apple fritter with Granny Smith apple filling and cinnamon glaze',
};

export const logoImage = { src: '/img/logo.webp', width: 325, height: 275, alt: 'MAD Donuts' };

const square = (src, alt) => ({ src, width: 800, height: 800, alt });

export const coreMenu = [
  {
    id: 'buttermilk-old-fashioned',
    name: 'Buttermilk Old-Fashioned',
    description: 'Buttermilk cake donut with a bourbon vanilla-bean glaze.',
    category: 'Cake donuts',
    image: square('/img/buttermilk-old-fashioned.webp', 'Buttermilk old-fashioned cake donut with bourbon vanilla-bean glaze'),
  },
  {
    id: 'apple-fritter',
    name: 'Apple Fritter',
    description: 'Our award-winning favourite: Granny Smith apple filling and cinnamon glaze.',
    category: 'Signature',
    image: square('/img/apple-fritter.webp', 'Apple fritter with Granny Smith apple filling and cinnamon glaze'),
  },
  {
    id: 'raspberry-cardamom',
    name: 'Raspberry-Cardamom',
    description: 'Brioche donut filled with house-made raspberry-cardamom jam, dusted with sugar.',
    category: 'Filled',
    image: square('/img/raspberry-cardamom.webp', 'Brioche donut filled with raspberry-cardamom jam'),
  },
  {
    id: 'strawberry-sumac',
    name: 'Strawberry-Sumac',
    description: 'Brioche donut filled with house-made strawberry-sumac jam, powdered sugar and sumac.',
    category: 'Filled',
    image: square('/img/strawberry-sumac.webp', 'Brioche donut filled with strawberry-sumac jam and dusted with sumac'),
  },
  {
    id: 'vanilla-bean-glazed',
    name: 'Vanilla-Bean Glazed',
    description: 'Classic brioche ring donut with bourbon vanilla glaze.',
    category: 'Classic',
    image: square('/img/vanilla-bean-glazed.webp', 'Brioche ring donut with bourbon vanilla glaze'),
  },
];

export const seasonalMenu = [
  {
    id: 'brown-sugar-bun',
    name: 'Brown Sugar Bun',
    description: 'Brioche scroll layered with cinnamon and dipped in brown sugar icing.',
    category: 'Seasonal',
    image: square('/img/brown-sugar-bun.webp', 'Brown sugar bun brioche scroll with cinnamon'),
  },
  {
    id: 'westchester-cream',
    name: 'Westchester Cream',
    description: 'Brioche donut filled with Bavarian cream, chocolate ganache, chocolate crumble and gold leaf.',
    category: 'Seasonal',
    image: square('/img/westchester-cream.webp', 'Westchester Cream donut with chocolate ganache and gold leaf'),
  },
  {
    id: 'everything-bagel',
    name: 'Everything Bagel',
    description: 'Brioche donut filled with whipped lemony cream cheese and everything bagel seasoning.',
    category: 'Seasonal',
    image: square('/img/everything-bagel.webp', 'Everything bagel donut filled with lemony cream cheese'),
  },
  {
    id: 'buttermilk-cider',
    name: 'Buttermilk Cider',
    description: 'Buttermilk cake donut infused with apple cider and dusted in cinnamon sugar — peak cider season only.',
    category: 'Seasonal',
    image: square('/img/buttermilk-cider.webp', 'Buttermilk cider cake donut dusted in cinnamon sugar'),
  },
  {
    id: 'biscoff',
    name: 'Biscoff',
    description: 'Brioche ring donut, vanilla glaze, Biscoff buttercream and crumble.',
    category: 'Seasonal',
    image: square('/img/biscoff.webp', 'Biscoff brioche ring donut with buttercream and crumble'),
  },
  {
    id: 'eggnog-old-fashioned',
    name: 'Eggnog Old-Fashioned',
    description: 'Buttermilk nutmeg cake donut with eggnog glaze and icing.',
    category: 'Seasonal',
    image: square('/img/eggnog-old-fashioned.webp', 'Eggnog old-fashioned cake donut with nutmeg'),
  },
  {
    id: 'babka-braid',
    name: 'Babka Braid',
    description: 'Chocolate-layered brioche braid, vanilla-bean glaze, chocolate ganache and crumble.',
    category: 'Seasonal',
    image: square('/img/babka-braid.webp', 'Chocolate babka braid with vanilla-bean glaze'),
  },
];

export const veganNote =
  'Most flavours are available as a vegan version — tell us which ones matter to your counter and we will build them into your order.';

export const allergenNotice =
  'We bake in a facility that handles nuts, dairy, soy, eggs, various flours and gluten. Cross-contact is limited by kitchen procedure, but please assume cross-contamination if you have severe food allergies. Ask us if you need detail on a specific flavour.';

export const cateringPackages = [
  {
    id: 'sweet-mornings',
    name: 'Sweet Mornings',
    description: 'Breakfast meetings and morning events.',
    servings: '12–15 people',
    includes: '24 assorted donuts, delivered in Westchester',
    price: 'Starting at $90',
    priceValue: 90,
  },
  {
    id: 'office-party-box',
    name: 'Office Party Box',
    description: 'Team celebrations and all-hands gatherings.',
    servings: '25–30 people',
    includes: '48 assorted donuts, delivered in Westchester',
    price: 'Starting at $160',
    priceValue: 160,
  },
  {
    id: 'grand-celebration',
    name: 'Grand Celebration',
    description: 'Weddings, launches and anything that needs a donut wall.',
    servings: '50+ people',
    includes: '100+ donuts, delivered in Westchester',
    price: 'Starting at $275',
    priceValue: 275,
  },
];

export const cafePartners = [
  { name: 'Climbing Wolf', location: 'Dobbs Ferry, NY', website: 'https://www.climbingwolf.com' },
  { name: 'Coffee Labs Roasters', location: 'Tarrytown + Eastchester, NY', website: 'https://www.coffeelabs.com' },
  { name: 'Ludy Cafe', location: 'Irvington, NY', website: 'https://www.ludycafe.com/' },
  { name: 'Shea Station', location: 'Pelham, NY', website: 'https://sheastationpelham.com/' },
  { name: 'Coffee for Good', location: 'Greenwich, CT', website: 'https://coffeeforgood.org/' },
  { name: 'Twisted Branch', location: 'Valhalla, NY', website: 'https://www.thetwistedbranch.com' },
  { name: 'Hudson Milk + Market', location: 'Peekskill, NY', website: 'https://hudsonmilk.com/themarket' },
  { name: "Mimi's Coffee House", location: 'Mt. Kisco, NY', website: 'https://mimiscoffeehouse.net/' },
  { name: 'Coffee Social', location: 'White Plains, NY', website: 'https://www.wpcoffeesocial.com/' },
  { name: 'BXVL Coffeehouse', location: 'Bronxville, NY', website: 'https://www.bxvlcoffeehouse.com' },
  { name: 'The Black Cow Coffee Co.', location: 'Pleasantville, NY', website: 'https://blackcow.com/' },
  { name: "Millie's Provisions", location: 'Larchmont, NY', website: 'https://www.milliesprovisions.com/' },
  { name: 'NY Hospitality Group', location: 'White Plains, NY', website: 'https://nyhgroup.com/' },
];

export const wholesaleSteps = [
  {
    title: 'Tell us what your counter needs',
    body: 'Send the inquiry form with your town and rough daily volume. We reply within one business day with what we can supply and when.',
  },
  {
    title: 'Agree the standing order',
    body: 'We set the flavours, the delivery mornings and the quantities together, then load them into your account so nothing has to be re-typed each week.',
  },
  {
    title: 'Order in your own portal',
    body: `Adjust quantities, add seasonal specials and skip a day whenever you need — orders close ${business.leadTime} before delivery.`,
  },
  {
    title: 'We bake overnight and deliver',
    body: 'Donuts are made to order the night before and delivered fresh for opening, with a photo record of every drop.',
  },
];

export const wholesaleProof = [
  {
    icon: 'award',
    title: 'Two-time award winner',
    body: 'Best of Westchester — Best Donuts, 2020 and 2023, judged by Westchester Magazine.',
  },
  {
    icon: 'sunrise',
    title: 'Baked overnight, delivered by opening',
    body: 'Made to order the night before. Nothing arrives from yesterday’s tray.',
  },
  {
    icon: 'store',
    title: `Stocked by ${cafePartners.length} local businesses`,
    body: 'Independent cafés, roasters and hospitality groups from Peekskill to Greenwich.',
  },
  {
    icon: 'leaf',
    title: 'Vegan versions and weekly specials',
    body: 'Most flavours come in a vegan version, plus a rotating seasonal list to keep the counter interesting.',
  },
];

export const wholesaleFaq = [
  {
    question: 'Which towns do you deliver wholesale donuts to?',
    answer: `We deliver across ${business.region} and ${business.nearby}, including ${serviceArea.slice(0, 8).join(', ')} and Greenwich, CT. If you are just outside, ask — routes change as we add partners.`,
  },
  {
    question: 'How far ahead do I need to order?',
    answer: `Standing orders close ${business.leadTime} before the delivery morning, which is the same deadline you will see in the ordering portal. Seasonal specials and bigger one-off runs are best flagged a week ahead.`,
  },
  {
    question: 'What is the minimum order?',
    answer: 'There is no fixed case minimum. We supply single-site cafés and multi-site groups alike, and agree a per-delivery minimum with you when we set up the account — send your expected volume and we will confirm.',
  },
  {
    question: 'How fresh are the donuts on arrival?',
    answer: 'Every donut is made to order overnight and delivered in the morning, so your counter opens with donuts baked hours earlier — never a day-old tray.',
  },
  {
    question: 'Can we get vegan donuts?',
    answer: veganNote,
  },
  {
    question: 'Do you handle unsold donuts?',
    answer: 'Yes. Guaranteed-returns accounts are credited for what does not sell, so you can stock confidently while you learn your counter. We will talk you through both options when you inquire.',
  },
  {
    question: 'Can you supply a one-off event instead?',
    answer: 'That is catering — three packages from 24 to 100+ donuts with delivery in Westchester. Request a quote on the catering page.',
  },
  {
    question: 'How do existing partners order?',
    answer: 'Through the wholesale client portal: standing orders, invoices, delivery photos and order history all live there. Use the "Client login" link in the navigation.',
  },
];

export const cateringFaq = [
  {
    question: 'How much notice do you need for catering?',
    answer: `Give us at least ${business.leadTime} for the smaller packages. Donut walls, large runs and anything over 100 donuts are best booked a week or more ahead.`,
  },
  {
    question: 'Is delivery included?',
    answer: 'Yes — delivery in Westchester is included in every package price. Tell us the town and the time you need them on the table.',
  },
  {
    question: 'Can we choose the flavours?',
    answer: 'Packages arrive as a baker’s assortment by default. Tell us what you want more of (or what to avoid) in the quote request and we will build the box around it.',
  },
  {
    question: 'Do you cater vegan guests?',
    answer: veganNote,
  },
];

export const aboutStory = [
  'MAD Donuts bakes in Pleasantville, Westchester, with one stubborn idea: a donut should be worth the calories. No shortcuts, no mixes, no trays of yesterday’s stock — just brioche and buttermilk doughs made from scratch, jams cooked in-house, and flavours that change with the season.',
  'That obsession earned Westchester Magazine’s Best of Westchester award for Best Donuts in 2020 and again in 2023, and it is why independent cafés from Peekskill to Greenwich put our name on their counter.',
  'Today the bakery runs as a wholesale kitchen: we bake overnight to order and deliver in the morning, so a café, restaurant or office gets donuts hours out of the fryer rather than a day-old delivery.',
];
